import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../../../services/axiosInterceptors';
import nipponBotApiEndpoints from '../../../services/nipponBotApiEndpoints';
import ResumePreviewDialog from './ResumePreviewDialog';
import CandidateFormDrawer from './CandidateFormDrawer';
import { theme as colorTheme } from '../../../Convoze.theme';

const primary = colorTheme('palette', 'primary').value;
const PRIMARY_HOVER = '#5f2ab8';

const primaryContainedButtonSx = {
  textTransform: 'none',
  bgcolor: primary,
  borderRadius: '12px',
  '&:hover': { bgcolor: PRIMARY_HOVER }
};

const primaryIconButtonSx = {
  color: primary,
  '&:hover': { bgcolor: `${primary}14` }
};

const tableHeadingSx = {
  textTransform: 'uppercase',
  fontWeight: 550,
  color: '#6b7280',
  fontSize: '0.7rem',
  backgroundColor: '#f9fafb',
  whiteSpace: 'nowrap'
};

const paginationSx = {
  '& .MuiPaginationItem-root': { color: primary, borderColor: primary },
  '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: `${primary} !important`, color: '#fff !important' },
  '& .MuiPaginationItem-root:hover': { backgroundColor: '#E0E7FF' }
};

const STATUS_STYLES = {
  completed: { color: '#1e7e34', bg: '#e6f4ea' },
  partial: { color: '#b45309', bg: '#fef3e2' },
  failed: { color: '#c62828', bg: '#fdecea' },
  processing: { color: '#0277bd', bg: '#e3f2fd' }
};

const DEFAULT_STATUS_STYLE = { color: '#616161', bg: '#f0f0f0' };

const LATEST_INTERVIEW_STYLES = {
  completed: { label: 'Completed', color: '#1e7e34', bg: '#e6f4ea' },
  pending: { label: 'Pending', color: '#0277bd', bg: '#e3f2fd' },
  queued: { label: 'Queued', color: '#7c4d00', bg: '#fff4d6' },
  calling: { label: 'Calling', color: '#7c4d00', bg: '#fff4d6' },
  submitted: { label: 'Submitted', color: '#5e35b1', bg: '#f0e7ff' },
  failed: { label: 'Failed', color: '#c62828', bg: '#fdecea' },
  none: { label: 'No interview scheduled', color: '#616161', bg: '#f0f0f0' }
};

const formatResumeStatusLabel = (status) => (status ? 'Uploaded' : 'Not uploaded');

const getLatestInterviewState = (candidate = {}) => {
  const latestStatus = String(candidate.latestInterviewStatus || '').toLowerCase();
  if (latestStatus) return latestStatus;
  return 'pending';
};

const formatStatusLabel = (status = '') => String(status || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (character) => character.toUpperCase());

const getLatestInterviewDisplay = (candidate = {}) => {
  const value = candidate.latestInterviewAt;
  if (!value) return { status: 'none', dateTime: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { status: 'none', dateTime: '' };
  return {
    status: getLatestInterviewState(candidate),
    dateTime: date.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
    })
  };
};

const formatDateTimeParts = (value) => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return { date: date.toLocaleDateString(), time: date.toLocaleTimeString() };
};

const DateTimeCell = ({ value }) => {
  const parts = formatDateTimeParts(value);
  if (!parts) return <TableCell sx={{ whiteSpace: 'nowrap' }}>-</TableCell>;
  return (
    <TableCell sx={{ whiteSpace: 'nowrap' }}>
      <Box>{parts.date}</Box>
      <Box sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>{parts.time}</Box>
    </TableCell>
  );
};

const TABLE_COLUMNS = [
  { label: 'Created On' },
  { label: 'Hiring Manager SAP Code' },
  { label: 'Candidate Name' },
  { label: 'Latest Interview' },
  { label: 'Resume Status' },
  { label: 'Actions' }
];

const CANDIDATE_PAGE_LIMIT = 10;

const normalizePaginator = ({ paginator = {}, rowCount = 0, requestedPage = 1, limit = CANDIDATE_PAGE_LIMIT }) => {
  const itemCount = Number(paginator.itemCount ?? paginator.totalDocs ?? rowCount);
  const perPage = Number(paginator.perPage ?? paginator.limit ?? limit);
  const currentPage = Number(paginator.currentPage ?? paginator.page ?? requestedPage);
  const pageCount = Number(paginator.pageCount ?? paginator.totalPages ?? Math.max(1, Math.ceil(itemCount / Math.max(perPage, 1))));

  return {
    ...paginator,
    itemCount,
    perPage,
    currentPage,
    pageCount
  };
};

export default function CandidatesTab({ onMessage, onComplete, selectedCandidateId, setSelectedCandidateId, initialAddOpen = false }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [paginationData, setPaginationData] = useState({});
  const [deletingId, setDeletingId] = useState('');
  const [previewResume, setPreviewResume] = useState(null);
  const [addDrawerOpen, setAddDrawerOpen] = useState(initialAddOpen);
  const [agents, setAgents] = useState([]);
  const token = localStorage.getItem('token');
  const navigate = useNavigate();
  const effectivePage = paginationData?.currentPage || page;
  const effectivePerPage = paginationData?.perPage || CANDIDATE_PAGE_LIMIT;
  const effectiveItemCount = paginationData?.itemCount || candidates.length;
  const showingFrom = candidates.length ? ((effectivePage - 1) * effectivePerPage) + 1 : 0;
  const showingTo = candidates.length ? Math.min(((effectivePage - 1) * effectivePerPage) + candidates.length, effectiveItemCount) : 0;

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const loadCandidates = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get(nipponBotApiEndpoints.candidateList, {
        headers: { Authorization: `Bearer ${token}` },
        params: { page, limit: CANDIDATE_PAGE_LIMIT, searchTerm: debouncedSearch }
      });
      const responsePayload = response?.data?.data || {};
      const candidateRows = Array.isArray(responsePayload?.data)
        ? responsePayload.data
        : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];
      const sortedCandidates = [...candidateRows].sort((a, b) =>
        (a.fullName || '').localeCompare(b.fullName || '', undefined, { sensitivity: 'base' })
      );
      setCandidates(sortedCandidates);
      setPaginationData(normalizePaginator({
        paginator: responsePayload?.paginator || response?.data?.paginator || {},
        rowCount: sortedCandidates.length,
        requestedPage: page
      }));
    } catch (error) {
      onMessage(error?.response?.data?.message || 'Failed to load candidate profiles.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCandidates();
  }, [page, debouncedSearch]);

  useEffect(() => {
    const loadAgents = async () => {
      try {
        const response = await axiosInstance.get(nipponBotApiEndpoints.elevenLabsAgents, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAgents((response?.data?.data || []).filter((agent) => agent.isActive));
      } catch (error) {
        onMessage(error?.response?.data?.message || 'Failed to load interview agents.', 'error');
      }
    };
    loadAgents();
  }, []);

  const handleViewCandidate = (candidate) => {
    setSelectedCandidateId(candidate.id);
    navigate(`/user/bot-interviews/candidates/${candidate.candidateUuid || candidate.id}`);
  };

  const handleEditCandidate = (candidate) => {
    setSelectedCandidateId(candidate.id);
    navigate(`/user/bot-interviews/candidates/${candidate.candidateUuid || candidate.id}?edit=1`);
  };

  const handleDeleteCandidate = async (candidate) => {
    const confirmed = window.confirm(`Delete candidate "${candidate.fullName || 'Unnamed Candidate'}"? This cannot be undone.`);
    if (!confirmed) return;

    setDeletingId(candidate.id);
    try {
      await axiosInstance.delete(`${nipponBotApiEndpoints.candidateDelete}/${candidate.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      onMessage('Candidate deleted successfully.', 'success');
      await loadCandidates();
      if (typeof onComplete === 'function') onComplete();
    } catch (error) {
      onMessage(error?.response?.data?.message || 'Failed to delete candidate.', 'error');
    } finally {
      setDeletingId('');
    }
  };

  const handleViewResume = (candidate) => {
    if (!candidate.resume?.id) return;
    if (candidate.resume.fileAvailable === false) return;
    setPreviewResume(candidate.resume);
  };

  const handleOpenAddDrawer = () => {
    setAddDrawerOpen(true);
    if (window.location.pathname !== '/user/bot-interviews/candidates/new') {
      navigate('/user/bot-interviews/candidates/new', { replace: false });
    }
  };

  const handleCloseAddDrawer = () => {
    setAddDrawerOpen(false);
    if (window.location.pathname === '/user/bot-interviews/candidates/new') {
      navigate('/user/bot-interviews/candidates', { replace: true });
    }
  };

  const handleCandidateCreated = async (candidate) => {
    if (candidate?.id) setSelectedCandidateId(candidate.id);
    await loadCandidates();
    if (typeof onComplete === 'function') onComplete();
  };

  return (
    <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
      <Paper sx={{ p: 3, boxShadow: 0, border: 1, borderColor: 'grey.200', borderRadius: 2, minWidth: 0, overflow: 'hidden' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
          <TextField
            size="small"
            label="Search Candidates"
            placeholder="Search by name, email, or SAP Code"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            sx={{ width: 280 }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDrawer}
            sx={primaryContainedButtonSx}
          >
            Add Candidate
          </Button>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" alignItems="center" sx={{ mb: 1 }}>
          {loading ? <CircularProgress size={18} /> : null}
        </Stack>

        {!candidates.length && !loading ? (
          <Alert severity="info">No candidates available yet.</Alert>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 560, overflowX: 'auto', overflowY: 'auto', width: '100%' }}>
              <Table size="small" stickyHeader sx={{ minWidth: 980 }}>
                <TableHead>
                  <TableRow>
                    {TABLE_COLUMNS.map((column) => (
                      <TableCell key={`${column.label}-${column.sub || ''}`} sx={{ ...tableHeadingSx, lineHeight: 1.3 }}>
                        <Box>{column.label}</Box>
                        {column.sub ? <Box>{column.sub}</Box> : null}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {candidates.map((candidate) => {
                    const latestInterview = getLatestInterviewDisplay(candidate);
                    const latestInterviewStyle = LATEST_INTERVIEW_STYLES[latestInterview.status] || {
                      label: formatStatusLabel(latestInterview.status),
                      color: '#455a64',
                      bg: '#eceff1'
                    };

                    return (
                      <TableRow key={candidate.id} hover selected={candidate.id === selectedCandidateId}>
                        <DateTimeCell value={candidate.createdAt} />
                        <TableCell>{candidate.sapCode || '-'}</TableCell>
                        <TableCell>{candidate.fullName || 'Unnamed Candidate'}</TableCell>
                        <TableCell sx={{ minWidth: 240 }}>
                          {latestInterview.status === 'none' ? (
                            <Chip
                              size="small"
                              label={latestInterviewStyle.label}
                              variant="outlined"
                              sx={{
                                fontWeight: 600,
                                borderWidth: 1.5,
                                borderColor: latestInterviewStyle.color,
                                color: latestInterviewStyle.color,
                                backgroundColor: latestInterviewStyle.bg
                              }}
                            />
                          ) : (
                            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                              <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                                {latestInterview.dateTime}
                              </Typography>
                              <Chip
                                size="small"
                                label={latestInterviewStyle.label}
                                variant="outlined"
                                sx={{
                                  height: 22,
                                  fontWeight: 700,
                                  borderWidth: 1.5,
                                  borderColor: latestInterviewStyle.color,
                                  color: latestInterviewStyle.color,
                                  backgroundColor: latestInterviewStyle.bg
                                }}
                              />
                            </Stack>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={formatResumeStatusLabel(candidate.resume?.id)}
                            variant="outlined"
                            sx={{
                              fontWeight: 600,
                              borderWidth: 1.5,
                              borderColor: (candidate.resume?.id ? STATUS_STYLES.completed : DEFAULT_STATUS_STYLE).color,
                              color: (candidate.resume?.id ? STATUS_STYLES.completed : DEFAULT_STATUS_STYLE).color,
                              backgroundColor: (candidate.resume?.id ? STATUS_STYLES.completed : DEFAULT_STATUS_STYLE).bg
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
                            <Tooltip title={!candidate.resume?.id ? 'No resume uploaded' : candidate.resume.fileAvailable === false ? 'Resume not available' : 'View / Download Resume'}>
                              <span>
                                <IconButton size="small" onClick={() => handleViewResume(candidate)} disabled={!candidate.resume?.id || candidate.resume.fileAvailable === false} sx={primaryIconButtonSx}>
                                  <DescriptionIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="View Candidate">
                              <IconButton size="small" onClick={() => handleViewCandidate(candidate)} sx={primaryIconButtonSx}>
                                <VisibilityIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Candidate">
                              <IconButton size="small" onClick={() => handleEditCandidate(candidate)} sx={primaryIconButtonSx}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Candidate">
                              <span>
                                <IconButton size="small" onClick={() => handleDeleteCandidate(candidate)} disabled={deletingId === candidate.id}>
                                  {deletingId === candidate.id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {candidates.length > 0
                  ? `Showing ${showingFrom}-${showingTo} of ${effectiveItemCount} results`
                  : 'No results found'}
              </Typography>
              <Pagination
                count={paginationData?.pageCount || 1}
                page={paginationData?.currentPage || page}
                onChange={(event, value) => setPage(value)}
                sx={paginationSx}
              />
            </Stack>
          </>
        )}
      </Paper>

      <ResumePreviewDialog open={Boolean(previewResume)} resume={previewResume} onClose={() => setPreviewResume(null)} />
      <CandidateFormDrawer
        open={addDrawerOpen}
        onClose={handleCloseAddDrawer}
        onSave={handleCandidateCreated}
        onMessage={onMessage}
        token={token}
        agents={agents}
      />
    </Box>
  );
}
